import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Collapse, Divider, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import LotteryBall from './LotteryBall';
import { checkSSQWinning, formatPrize } from '../utils/lotteryUtils';

/**
 * 彩票卡片组件
 */
function LotteryCard({ order }) {
  const [expanded, setExpanded] = useState(false);

  const {
    orderNo,
    period,
    redBalls,
    blueBall,
    betAmount,
    betTime,
    status,
    winningNumbers,
    prize = 0
  } = order;

  // 计算中奖信息
  const winInfo = status !== 'pending' && winningNumbers
    ? checkSSQWinning(redBalls, blueBall, winningNumbers.redBalls, winningNumbers.blueBall)
    : null;

  // 状态标签配置
  const statusConfig = {
    pending: { label: '⏳ 待开奖', color: '#FF9800', bgColor: '#FFF3E0' },
    won: { label: winInfo ? `🏆 ${winInfo.name}` : '🏆 中奖', color: '#E53935', bgColor: '#FFEBEE' },
    lost: { label: '❌ 未中奖', color: '#757575', bgColor: '#F5F5F5' }
  };

  const currentStatus = statusConfig[status] || statusConfig.pending;

  // 格式化时间
  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        overflow: 'hidden',
        borderRadius: 2
      }}
    >
      {/* 卡片头部 */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          background: status === 'won'
            ? 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)'
            : '#FFFFFF'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333' }}>
              第 {period} 期
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              {formatTime(betTime)}
            </Typography>
          </Box>
          <Chip
            label={currentStatus.label}
            size="small"
            sx={{
              backgroundColor: currentStatus.bgColor,
              color: currentStatus.color,
              fontWeight: 600,
              fontSize: 12
            }}
          />
        </Box>

        {/* 投注号码 */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#666' }}>
            投注号码
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {redBalls?.map(num => {
              const isMatched = winInfo && winningNumbers?.redBalls?.includes(num);
              return (
                <LotteryBall
                  key={num}
                  number={num}
                  type="red"
                  selected={true}
                  size="small"
                  matched={isMatched}
                  disabled={true}
                />
              );
            })}
            <Box sx={{ width: 8, height: 24, borderLeft: '2px dashed #E0E0E0', mx: 0.5 }} />
            <LotteryBall
              number={blueBall}
              type="blue"
              selected={true}
              size="small"
              matched={winInfo?.matchBlue === 1}
              disabled={true}
            />
          </Box>
        </Box>

        {/* 中奖金额显示 */}
        {status === 'won' && prize > 0 && (
          <Box
            sx={{
              mt: 1,
              p: 1,
              backgroundColor: '#FFEBEE',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" sx={{ color: '#E53935', fontWeight: 600 }}>
              🎉 恭喜中奖
            </Typography>
            <Typography variant="h6" sx={{ color: '#E53935', fontWeight: 700 }}>
              ¥{formatPrize(prize)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 展开/收起按钮 */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          borderTop: '1px solid #F0F0F0',
          py: 0.5
        }}
      >
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ color: '#999' }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* 展开详情 */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, pt: 0, backgroundColor: '#FAFAFA' }}>
          <Divider sx={{ mb: 2 }} />

          {/* 订单号 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#999' }}>订单号</Typography>
            <Typography variant="body2" sx={{ color: '#333', fontFamily: 'monospace' }}>
              {orderNo}
            </Typography>
          </Box>

          {/* 投注金额 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#999' }}>投注金额</Typography>
            <Typography variant="body2" sx={{ color: '#333' }}>
              ¥{(betAmount / 100).toFixed(2)}
            </Typography>
          </Box>

          {/* 开奖号码（已开奖的显示） */}
          {status !== 'pending' && winningNumbers && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                开奖号码
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {winningNumbers.redBalls?.map(num => (
                  <LotteryBall
                    key={num}
                    number={num}
                    type="red"
                    selected={true}
                    size="small"
                    disabled={true}
                  />
                ))}
                <Box sx={{ width: 8, height: 24, borderLeft: '2px dashed #E0E0E0', mx: 0.5 }} />
                <LotteryBall
                  number={winningNumbers.blueBall}
                  type="blue"
                  selected={true}
                  size="small"
                  disabled={true}
                />
              </Box>

              {/* 中奖详情 */}
              {winInfo && (
                <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#FFFFFF', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    命中：{winInfo.matchRed} 个红球 + {winInfo.matchBlue} 个蓝球
                    {winInfo.level && ` · ${winInfo.name}`}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default React.memo(LotteryCard);
